import React, { useState, useMemo, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUserRecognitions } from '@/features/profile/hooks/useUserRecognitions';
import { useMostRecognizedTechniques } from '@/features/social/hooks/useMostRecognizedTechniques';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import GiftIcon from '@/assets/icons/gift.svg';
import { useI18n } from '@/contexts/I18nContext';
import { useAuth } from '@/contexts/AuthContext';
import { getTechniqueName } from '@/shared/hooks/useTechniques';

interface Recognition {
  id: string;
  praiser: {
    id: string;
    full_name: string;
    username: string;
    avatar_url?: string;
    belt_rank?: string;
  };
  feedback_text: string;
  created_at: string;
  techniques: Array<{
    id: number;
    name: string;
    name_br?: string | null;
  }>;
}

interface RecognizedTechnique {
  id: number;
  name: string;
  recognition_count: number;
}

export const UserRecognitions: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t, currentLanguage } = useI18n();
  const { user } = useAuth();
  const isOwnProfile = user?.id === userId;
  // Default to 'techniques' tab when viewing other users' profiles
  const [activeTab, setActiveTab] = useState<'recognitions' | 'techniques'>(isOwnProfile ? 'recognitions' : 'techniques');
  
  const { data: recognitions, isLoading: recognitionsLoading, error: recognitionsError } = useUserRecognitions(userId || '');
  const { data: mostRecognizedTechniques, isLoading: techniquesLoading, error: techniquesError } = useMostRecognizedTechniques(userId || '');
  
  // Ensure we're on the correct tab when profile changes
  useEffect(() => {
    if (!isOwnProfile) {
      setActiveTab('techniques');
    }
  }, [isOwnProfile]);
  
  // Extract unique techniques from all recognitions (for other users' profiles)
  const allTechniquesFromRecognitions = useMemo(() => {
    if (!recognitions || isOwnProfile) return [];
    
    const techniqueMap = new Map<number, { id: number; name: string; name_br?: string | null; count: number }>();
    
    recognitions.forEach((recognition) => {
      recognition.techniques?.forEach((technique) => {
        const existing = techniqueMap.get(technique.id);
        if (existing) {
          existing.count += 1;
        } else {
          techniqueMap.set(technique.id, {
            id: technique.id,
            name: technique.name,
            name_br: (technique as { id: number; name: string; name_br?: string | null }).name_br,
            count: 1
          });
        }
      });
    });
    
    return Array.from(techniqueMap.values()).sort((a, b) => b.count - a.count);
  }, [recognitions, isOwnProfile]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const handleBack = (): void => {
    navigate(-1);
  };

  const isLoading = recognitionsLoading || techniquesLoading;
  const hasError = recognitionsError || techniquesError;
  const hasRecognitions = recognitions && recognitions.length > 0;
  const hasTechniques = mostRecognizedTechniques && mostRecognizedTechniques.length > 0;
  // For other users' profiles, check if we have techniques from recognitions or from mostRecognizedTechniques
  const hasTechniquesForOtherUser = !isOwnProfile && (allTechniquesFromRecognitions.length > 0 || (mostRecognizedTechniques && mostRecognizedTechniques.length > 0));
  const showContent = hasRecognitions || hasTechniques || hasTechniquesForOtherUser;

  // Most Recognized Techniques Section Component - Only rendered when there is data or loading
  const MostRecognizedTechniquesSection: React.FC = () => {
    if (!isLoading && !hasTechniques) return null;
    return (
      <div className="mb-6" style={{ paddingLeft: '20px', paddingRight: '20px', width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-sm font-semibold text-white">{t('recognitions.most_recognized_techniques')}</h2>
          {!isLoading && hasTechniques && (
            <button
              onClick={() => setActiveTab('techniques')}
              className="text-white text-xs font-medium underline hover:text-white/80 transition-colors cursor-pointer"
            >
              {t('calendar.see_all')}
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="flex flex-wrap gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-[#281B1B] rounded-full px-3 py-1 animate-pulse">
                <div className="w-20 h-6 bg-[#3A2A2A] rounded"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {mostRecognizedTechniques.slice(0, 5).map((technique) => (
              <div key={technique.id} className="bg-sossiety-red text-white rounded-full px-3 py-1 flex items-center gap-2">
                <div className="w-4 h-4 bg-white rounded-full flex items-center justify-center">
                  <span className="text-black text-[10px] font-bold">{technique.recognition_count}</span>
                </div>
                <span className="text-sm font-medium">{getTechniqueName({ id: technique.id, name: technique.name, name_br: technique.name_br || undefined, category: '' }, currentLanguage).replace(/\(e\.g.*?\)/gi, '').trim()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <TopNavigation variant="subtle" />
      <div className="min-h-screen bg-[#160C0C] text-white" style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
        {/* Header - Always visible */}
        <div className="pt-16" style={{ width: '100%', maxWidth: '100vw' }}>
          <div className="flex items-center justify-between py-4 border-b border-[#281B1B] box-border" style={{ paddingLeft: '20px', paddingRight: '20px', width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
            <button onClick={handleBack} className="p-1 text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold truncate max-w-[60%]">{t('profile.recognitions')}</h1>
          </div>

          {/* Content - Always visible structure */}
          <div className="pt-2 pb-4 box-border" style={{ width: '100%', maxWidth: '100vw' }}>
            {/* Most Recognized Techniques Section - Always rendered the same way */}
            <MostRecognizedTechniquesSection />

          {/* Tabs - Always show when we have data or are loading */}
          {(showContent || isLoading) && (
            <div style={{ paddingLeft: '20px', paddingRight: '20px', width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
              <div className="flex border-b-2 border-white/20 mb-4">
                {/* Only show Recognitions tab for own profile */}
                {isOwnProfile && (
                  <button
                    onClick={() => setActiveTab('recognitions')}
                    className={`flex-1 pb-2 px-1 text-xs font-semibold ${
                      activeTab === 'recognitions'
                        ? 'text-white border-b-2 border-sossiety-red'
                        : 'text-white/60 border-b-2 border-transparent'
                    }`}
                  >
                    {t('recognitions.recognitions_tab')}
                  </button>
                )}
                <button 
                  onClick={() => setActiveTab('techniques')} 
                  className={`${isOwnProfile ? 'flex-1' : 'w-full'} pb-2 px-1 text-xs font-semibold ${
                    activeTab === 'techniques' 
                      ? 'text-white border-b-2 border-sossiety-red' 
                      : 'text-white/60 border-b-2 border-transparent'
                  }`}
                >
                  {t('recognitions.techniques_tab')}
                </button>
              </div>

              {/* Tab Content - Always show structure */}
              {activeTab === 'recognitions' && (
                <div className="space-y-4 min-h-[200px]">
                  {isLoading ? (
                    // Loading skeleton for recognitions
                    <>
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-[#281B1B] rounded-2xl p-4 border border-[#3A2A2A] animate-pulse">
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 bg-[#3A2A2A] rounded-full"></div>
                            <div className="flex-1">
                              <div className="h-4 bg-[#3A2A2A] rounded w-32 mb-2"></div>
                              <div className="h-3 bg-[#3A2A2A] rounded w-24 mb-2"></div>
                              <div className="h-3 bg-[#3A2A2A] rounded w-full"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : hasError ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <p className="text-white/60">{t('recognitions.error_loading_recognitions')}</p>
                    </div>
                  ) : hasRecognitions ? (
                    // If viewing other user's profile, show only techniques
                    !isOwnProfile ? (
                      allTechniquesFromRecognitions.length > 0 ? (
                        allTechniquesFromRecognitions.map((technique) => (
                          <div key={technique.id} className="bg-[#281B1B] rounded-2xl p-4 border border-[#3A2A2A]">
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="w-10 h-10 bg-sossiety-red rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-white text-sm font-bold">
                                    {technique.name.charAt(0)}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0 overflow-hidden">
                                  <h3 className="text-white font-semibold text-sm break-words line-clamp-2">
                                    {getTechniqueName({ id: technique.id, name: technique.name, name_br: technique.name_br || undefined, category: '' }, currentLanguage).replace(/\(e\.g.*?\)/gi, '').trim()}
                                  </h3>
                                  <p className="text-white/60 text-xs mt-1">
                                    {technique.count} {technique.count !== 1 ? t('recognitions.recognition_plural') : t('recognitions.recognition_singular')}
                                  </p>
                                </div>
                              </div>
                              <div className="bg-sossiety-red text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0">
                                {technique.count}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <div className="w-16 h-16 bg-[#281B1B] rounded-full flex items-center justify-center mb-4">
                            <span className="text-sossiety-red text-2xl">🥋</span>
                          </div>
                          <h3 className="text-white font-semibold text-lg mb-2">{t('recognitions.no_techniques_recognized_yet')}</h3>
                          <p className="text-white/60 text-sm max-w-xs">
                            {t('recognitions.techniques_empty_state_subtitle')}
                          </p>
                        </div>
                      )
                    ) : (
                      // Own profile: show full recognitions with feedback text
                      recognitions.map((recognition) => (
                        <div key={recognition.id} className="bg-[#281B1B] rounded-2xl p-4 border border-[#3A2A2A]">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1">
                              {/* Framed Avatar */}
                              <div className="flex-shrink-0">
                                <FramedAvatar
                                  src={recognition.praiser.avatar_url}
                                  alt={recognition.praiser.full_name}
                                  initials={recognition.praiser.full_name.charAt(0)}
                                  size={40}
                                  frameSize={{ w: 50, h: 55 }}
                                  beltRank={recognition.praiser.belt_rank || 'white'}
                                  showFrame={true}
                                  username={recognition.praiser.username}
                                />
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <span className="text-white font-semibold text-sm block">
                                  {recognition.praiser.full_name}
                                </span>
                                <span className="text-white/60 text-xs block mb-2">
                                  @{recognition.praiser.username}
                                </span>
                                <p className="text-white/80 text-sm">
                                  {recognition.feedback_text}
                                </p>
                              </div>
                            </div>
                            
                            {/* Date */}
                            <span className="text-white/60 text-xs flex-shrink-0">
                              {formatDate(recognition.created_at)}
                            </span>
                          </div>
                        </div>
                      ))
                    )
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-16 h-16 bg-[#281B1B] rounded-full flex items-center justify-center mb-4">
                        <span className="text-sossiety-red text-2xl">🏆</span>
                      </div>
                      <h3 className="text-white font-semibold text-lg mb-2">{t('recognitions.no_recognitions_yet')}</h3>
                      <p className="text-white/60 text-sm max-w-xs">
                        {isOwnProfile 
                          ? t('recognitions.empty_state_subtitle')
                          : t('recognitions.empty_state_subtitle_other_user')
                        }
                      </p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'techniques' && (
                <div className="space-y-4 min-h-[200px]">
                  {isLoading ? (
                    // Loading skeleton for techniques
                    <>
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-[#281B1B] rounded-2xl p-4 border border-[#3A2A2A] animate-pulse">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-[#3A2A2A] rounded-full"></div>
                              <div>
                                <div className="h-4 bg-[#3A2A2A] rounded w-32 mb-2"></div>
                                <div className="h-3 bg-[#3A2A2A] rounded w-24"></div>
                              </div>
                            </div>
                            <div className="w-8 h-8 bg-[#3A2A2A] rounded-full"></div>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : hasError ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <p className="text-white/60">{t('recognitions.error_loading_recognitions')}</p>
                    </div>
                  ) : (hasTechniques || hasTechniquesForOtherUser) ? (
                    // For other users, prefer mostRecognizedTechniques (if available), otherwise use techniques from recognitions; for own profile, use mostRecognizedTechniques
                    (isOwnProfile 
                      ? mostRecognizedTechniques 
                      : (mostRecognizedTechniques && mostRecognizedTechniques.length > 0 ? mostRecognizedTechniques : allTechniquesFromRecognitions)
                    ).map((technique) => {
                      // Normalize the count field (mostRecognizedTechniques uses recognition_count, allTechniquesFromRecognitions uses count)
                      const count = 'recognition_count' in technique ? technique.recognition_count : technique.count;
                      const nameBr = 'name_br' in technique ? technique.name_br : undefined;
                      
                      return (
                        <div key={technique.id} className="bg-[#281B1B] rounded-2xl p-4 border border-[#3A2A2A]">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <div className="w-10 h-10 bg-sossiety-red rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-white text-sm font-bold">
                                  {technique.name.charAt(0)}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0 overflow-hidden">
                                <h3 className="text-white font-semibold text-sm break-words line-clamp-2">{getTechniqueName({ id: technique.id, name: technique.name, name_br: nameBr || undefined, category: '' }, currentLanguage).replace(/\(e\.g.*?\)/gi, '').trim()}</h3>
                                <p className="text-white/60 text-xs mt-1">
                                  {count} {count !== 1 ? t('recognitions.recognition_plural') : t('recognitions.recognition_singular')}
                                </p>
                              </div>
                            </div>
                            <div className="bg-sossiety-red text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0">
                              {count}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-16 h-16 bg-[#281B1B] rounded-full flex items-center justify-center mb-4">
                        <span className="text-sossiety-red text-2xl">🥋</span>
                      </div>
                      <h3 className="text-white font-semibold text-lg mb-2">{t('recognitions.no_techniques_recognized_yet')}</h3>
                      <p className="text-white/60 text-sm max-w-xs">
                        {isOwnProfile 
                          ? t('recognitions.techniques_empty_state_subtitle')
                          : t('recognitions.techniques_empty_state_subtitle_other_user')
                        }
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Empty State - Only show when not loading and no data */}
          {!isLoading && !showContent && !hasError && (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
              {/* Empty State Icon */}
              <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
                <img 
                  src={GiftIcon} 
                  alt="Recognitions" 
                  className="w-10 h-10 text-white/40"
                  style={{ filter: 'brightness(0) invert(1) opacity(0.4)' }}
                />
              </div>
              
              {/* Empty State Title */}
              <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
                {t('recognitions.no_recognitions_yet')}
              </h3>
              
              {/* Empty State Subtitle */}
              <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
                {isOwnProfile 
                  ? t('recognitions.empty_state_subtitle')
                  : t('recognitions.empty_state_subtitle_other_user')
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}; 